import React, { useMemo, useState } from "react";
import "./bill-duplicate.style.scss";
import { FaCheck, FaPrint } from "react-icons/fa6";

// global functions
import { isEmptyObject } from "../../global/function";

// components
import EmptyRecord from "../empty-record/empty-record.component";
import FormError from "../form-error/form-error.component";
import SelectBox from "../form-inputs/select-box.component";
import StructuredSideModal from "../structured-side-modal/structured-side-modal.component";
import { Button } from "../ui/button";

// queries
import { useBillsForSelection, useIncrementPrintCount, usePrintBulkBills, PRINT_SIZES } from "../../queries/bill.queries";
import type { Bill, BillSearchFilters, PrintSize } from "../../queries/bill.queries";
import type { BilledItem } from "../invoice-template/invoice-template.component";
import { useLastPrintSize } from "../../hooks/useLastPrintSize";

interface BillBulkPrintProps {
	isOpen: boolean;
	onBulkPrintModalClose: () => void;
	// Fires right before the modal closes on a successful print — parent owns
	// the notice since this panel unmounts immediately after (see onSelectBill
	// call below), too soon for an InlineNotice mounted in here to be seen.
	onPrintSuccess: () => void;
	filterValues: BillSearchFilters;
	onSelectBill: (bill: Record<string, never>) => void;
}

// A bill selected here (possibly re-shaped to pull credit_note.billed_items
// up to billed_items for a voided bill) — same reshape BillDuplicate applies.
type PrintableBill = Omit<Bill, "billed_items"> & { billed_items?: BilledItem[] };

function BillBulkPrint({ isOpen, onBulkPrintModalClose, onPrintSuccess, filterValues, onSelectBill }: BillBulkPrintProps) {
	const { data, isLoading } = useBillsForSelection(filterValues);
	const reduxBillList = useMemo(() => data?.bill ?? [], [data]);

	const printMutation = useIncrementPrintCount();
	const printBulkMutation = usePrintBulkBills();
	const [printSize, setPrintSize] = useLastPrintSize();

	const [billsToPrint, setBillsToPrint] = useState<Record<number, PrintableBill>>({});

	const billList = useMemo(() => reduxBillList.filter((bill) => bill.ird_bill === "success"), [reduxBillList]);

	const billFilter = useMemo(() => {
		let billVoid: string;
		if (filterValues?.bill_void === true) {
			billVoid = "Voided Bills";
		} else if (filterValues?.bill_void === false) {
			billVoid = "Non-void Bills";
		} else {
			billVoid = "";
		}

		const filterObject: Record<string, unknown> = { ...filterValues, bill_void: billVoid };

		for (const key in filterObject) {
			if (filterObject[key] === "") {
				delete filterObject[key];
			}
		}

		if (filterObject.total_sales_max === 0) {
			delete filterObject.total_sales_min;
			delete filterObject.total_sales_max;
		}
		return { ...filterObject };
	}, [filterValues]);

	const handleBillSelection = (bill: Bill, index: number) => {
		const state = { ...billsToPrint };
		if (state[index]) {
			delete state[index];
		} else {
			state[index] = bill.bill_void
				? { ...bill, billed_items: bill.credit_note?.billed_items }
				: { ...bill };
		}
		setBillsToPrint(state);
	};

	const handleToggleAll = () => {
		let objState: Record<number, PrintableBill>;
		if (Object.keys(billsToPrint).length === billList.length) {
			objState = {};
		} else {
			objState = billList.reduce((acc: Record<number, PrintableBill>, value, index) => {
				acc[index] = value.bill_void
					? { ...value, billed_items: value.credit_note?.billed_items }
					: value;
				return acc;
			}, {});
		}
		setBillsToPrint(objState);
	};

	const handleBulkPrint = async () => {
		// The BE bulk-PDF endpoint renders one page per bill_id only — it has no
		// concept of also rendering a voided bill's credit note (unlike the old
		// hidden-template approach, which rendered both). So the print-count
		// payload here only bumps the bill itself, not its credit_note_id too —
		// bumping a count for a document that wasn't actually printed would be
		// inaccurate. Credit notes still get their own count via the single-bill
		// print flow (invoice-bill.page.tsx).
		const flatmapPayloadArray = Object.values(billsToPrint).flatMap((bill) => {
			if (bill.ird_bill !== "success" && !bill.bill_canceled) {
				return [];
			}
			return [{ id: bill.bill_id, model_type: "bill" as const, print_type: "print" as const }];
		});
		const billIds = flatmapPayloadArray.map((entry) => entry.id);
		if (billIds.length === 0) return;

		try {
			await printBulkMutation.mutateAsync({ billIds, size: printSize });
			await printMutation.mutateAsync({ updates: flatmapPayloadArray });
			onSelectBill({});
			onPrintSuccess();
			onBulkPrintModalClose();
		} catch {
			// FormError (bound to printBulkMutation/printMutation) renders the message
		}
	};

	return (
		<StructuredSideModal
			open={isOpen}
			onOpenChange={(open) => { if (!open) onBulkPrintModalClose(); }}
			title="Print Bills"
			width={560}
			footer={
				<div className="flex items-center justify-end gap-2">
					<SelectBox
						aria-label="Print size"
						options={PRINT_SIZES.map(({ id, label }) => ({ id, value: label }))}
						value={printSize}
						onChange={(e) => setPrintSize(e.target.value as PrintSize)}
						disabled={printBulkMutation.isPending}
					/>
					<Button
						size="sm"
						onClick={handleBulkPrint}
						disabled={isEmptyObject(billsToPrint) || printBulkMutation.isPending}
					>
						<FaPrint />
						{printBulkMutation.isPending ? "Printing..." : "Print Bills"}
					</Button>
				</div>
			}
		>
		<div className="bill-duplicate-container">
			<div className="modal-body">
				<FormError
					isError={printBulkMutation.isError || printMutation.isError}
					error={printBulkMutation.error ?? printMutation.error}
					fallback="Failed to print bills."
				/>
				<div className="bill-filters-wrapper">
					<h2 className="section-header">Filter Values</h2>
					<div className="bill-filter-container">
						{Object.values(billFilter).map((value, i) => (
							<div key={i} className="bill-filter-container-inner">
								<span>{String(value)}</span>
							</div>
						))}
					</div>
				</div>
				<div className="divider" />

				{!isLoading && billList.length > 0 ? (
					<div className="bills-wrapper">
						<h2 className="section-header">Filtered Bills</h2>
						<p className="section-description">
							Select all or some of the filtered bills to print. <b>IRD Sync error</b> bills will not
							be printed
						</p>
						<div className="bill-container" onClick={handleToggleAll}>
							<div
								className={`bill-select ${
									Object.keys(billsToPrint).length === billList.length ? "selected" : ""
								}`}
							>
								{Object.keys(billsToPrint).length === billList.length && (
									<FaCheck style={{ color: "#46BE46", fontSize: "12px" }} />
								)}
							</div>
							<div className="select-btn">
								{Object.keys(billsToPrint).length === billList.length ? "Deselect All" : "Select All"}
							</div>
						</div>
						{billList.map((bill, i) => (
							<div className="bill-container" key={bill.bill_id} onClick={() => handleBillSelection(bill, i)}>
								<div className={`bill-select ${billsToPrint[i] ? "selected" : ""}`}>
									{billsToPrint[i] && (
										<FaCheck style={{ color: "#46BE46", fontSize: "12px" }} />
									)}
								</div>

								<div className="bill-container-inner">
									<div className="bill-details">
										<h4 className="bill-client">{bill.buyer_name}</h4>
										<div className="bill-tag-wrapper">
											<div className={`bill-status ${bill.bill_void ? "credited" : "bill"}`}>
												{bill.bill_void ? "Credit Note" : "Bill"}
											</div>
											{bill.bill_canceled ? (
												<div className="bill-status canceled">Canceled</div>
											) : (
												bill.ird_bill !== "success" && (
													<div className="bill-status credited">IRD Sync Error</div>
												)
											)}
										</div>
									</div>
									<div className="bill-details">Rs. {bill.sub_total?.toFixed(2)}</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<div>
						<EmptyRecord caption={isLoading ? "Loading bills..." : "No bills to print"} />
					</div>
				)}
			</div>
		</div>
		</StructuredSideModal>
	);
}

export default BillBulkPrint;
